import React, { useEffect, useRef, useState } from "react";
import { Car, CalendarDays, CalendarCheck, Clock } from "lucide-react";
import ModalSearch from "../../components/modal-search/ModalSearch";
import Search from "../../components/search/Search";
import { useAuth } from "../../context/AuthContext";
import useBuscaViagens from "../../hooks/UseBuscaViagens/UseBuscaViagens";
import UseCreateTripNegotiations from "../../hooks/UseTripsNegotiations/UseCreateTripNegotiations";

import "./Busca.css";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 980);

  useEffect(() => {
    document.body.classList.add("search-results-page");

    return () => {
      document.body.classList.remove("search-results-page");
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 980);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
}

function formatMilhar(numero) {
  if (numero === null || numero === undefined || numero === "") return "0";

  const n = Number(numero);
  if (Number.isNaN(n)) return "0";

  return n.toLocaleString("pt-BR");
}

function formatPrice(valor) {
  if (valor === null || valor === undefined || valor === "") {
    return { inteiro: "0", centavos: "00" };
  }

  let numero = 0;

  if (typeof valor === "number") {
    numero = valor;
  } else {
    const numeroLimpo = String(valor)
      .replace("R$", "")
      .replace(/\s/g, "")
      .replace(/\./g, "")
      .replace(",", ".");

    numero = Number(numeroLimpo);
  }

  if (Number.isNaN(numero) || !Number.isFinite(numero)) {
    return { inteiro: "0", centavos: "00" };
  }

  const [inteiro, centavos] = numero.toFixed(2).split(".");
  return { inteiro, centavos };
}

function LoadingOverlay() {
  return (
    <>
      <style>{`
        .loading-overlay {
          position: fixed;
          inset: 0;
          z-index: 3000;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(7, 18, 33, 0.62);
          backdrop-filter: blur(10px);
        }

        .loader-container {
          position: relative;
          width: 180px;
          height: 180px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .loader-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 4px solid rgba(255,255,255,0.06);
        }

        .loader-progress {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 4px solid transparent;
          border-top: 4px solid #19c4c9;
          border-right: 4px solid #19c4c9;
          animation: rotate 1.4s linear infinite;
          filter: drop-shadow(0 0 8px #19c4c9) drop-shadow(0 0 18px #19c4c9);
        }

        .loader-text {
          color: #19e6ed;
          font-weight: 700;
          letter-spacing: 3px;
          font-size: 14px;
          text-shadow: 0 0 8px #19c4c9, 0 0 20px #19c4c9;
          animation: pulse 1.6s ease-in-out infinite;
        }

        @keyframes rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%,100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

      `}</style>

      <div className="loading-overlay">
        <div className="loader-container">
          <div className="loader-ring"></div>
          <div className="loader-progress"></div>
          <div className="loader-text">BUSCANDO</div>
        </div>
      </div>
    </>
  );
}

function renderCard(item, onBuyTrip, loadingId, searchData) {
  const price = formatPrice(item.preco_estimado);
  const isBuying = loadingId === (item.id || item.serie_viagem);

  return (
    <div className="search-card" key={item.id || item.serie_viagem}>
      <div className="profile-box">
        <img
          src={item.imagem_usuario || "https://i.pravatar.cc/100"}
          alt="perfil"
          className="profile-image"
        />
        <h3 style={{ marginTop: 5 }}>{item.nome_usuario || "Sem nome"}</h3>
        <br />
      </div>

      <div className="info-box">
        <div className="route-tags-row">
          <div className="route-container">
            <div className="location-badge">
              {item.cidade_de_onde_vai_sair || "-"}
            </div>

            <div className="icon-box">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1cb5e0"
                strokeWidth="3"
              >
                <path d="M7 7l10 10M17 7v10H7" />
              </svg>
            </div>

            <div className="location-badge">
              {item.cidade_para_onde_vai || "-"}
            </div>
          </div>

          <div className="tags">
            <div className="tags-row">
              <span className="tag-item">
                <Car size={16} strokeWidth={2.5} className="tag-icon" />
                {item.meio_transporte}
              </span>

              <span className="tag-item">
                <CalendarDays
                  size={16}
                  strokeWidth={2.5}
                  className="tag-icon"
                />
                {item.duracao_viagem}
              </span>
            </div>
          </div>
        </div>

        <div className="dates">
          <div className="date-group">
            <small>SAÍDA</small>
            <div className="date-content">
              <CalendarCheck size={13} className="blue-icon" />
              <span className="date-text">
                {item.data_partida_para_busca || "-"} às
              </span>
              <div className="time-badge">
                <span>{item.horas_partida || "-"}</span>
              </div>
            </div>
          </div>

          <div className="date-group">
            <small>CHEGADA</small>
            <div className="date-content">
              <CalendarCheck size={13} className="blue-icon" />
              <span className="date-text">
                {item.data_chegada_para_busca || "-"} às
              </span>
              <div className="time-badge">
                <span>{item.horas_chegada || "-"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="price-box">
        <div className="doc-title">
          {searchData?.encomenda || item.tipo_encomenda || ""}
        </div>

        <div className="price-value">
          <span className="currency">R$</span>

          <span className="number">
            {formatMilhar(price.inteiro)}
            <span className="cents">,{price.centavos}</span>
          </span>
        </div>

        <button
          onClick={() => onBuyTrip(item)}
          disabled={isBuying}
          style={{
            marginTop: 20,
            fontSize: 12,
            width: "60%",
            maxWidth: 200,
            opacity: isBuying ? 0.7 : 1,
            cursor: isBuying ? "not-allowed" : "pointer",
          }}
        >
          {isBuying ? "Processando..." : "Comprar"}
        </button>
      </div>

      <div className="description">
        {item.informacao_sobre_trajeto || "Sem descrição"}
      </div>
    </div>
  );
}

export default function Busca() {
  const lastModalSearchKeyRef = useRef(null);

  const isMobile = useIsMobile();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showNoTravelerModal, setShowNoTravelerModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  const [notificationChoice, setNotificationChoice] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");

  const { handleSearchClick, RenderModal } = ModalSearch();

  const { user } = useAuth();

  const {
    searchData,
    recommendedTrips,
    otherTrips,
    loading,
    error,
    recommendedSort,
    setRecommendedSort,
    cityMode,
    setCityMode,
  } = useBuscaViagens();

  const { buyTrip, loadingId } = UseCreateTripNegotiations();

  const hasRecommended = recommendedTrips.length > 0;
  const hasOther = otherTrips.length > 0;

  const searchKey = JSON.stringify({
    origem: searchData?.origem || "",
    destino: searchData?.destino || "",
    data: searchData?.data || "",
    encomenda: searchData?.encomenda || "",
    seguro: searchData?.seguro || "",
  });

  useEffect(() => {
    setShowNoTravelerModal(false);
    setShowNotificationModal(false);
    setShowSuccessModal(false);
    setNotificationChoice("");
  }, [searchKey]);

  useEffect(() => {
    if (loading || error) return;

    const shouldShowModal =
      recommendedTrips.length === 0 && otherTrips.length > 0;

    if (!shouldShowModal) return;
    if (lastModalSearchKeyRef.current === searchKey) return;

    lastModalSearchKeyRef.current = searchKey;

    const timer = setTimeout(() => {
      setShowNoTravelerModal(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [loading, error, recommendedTrips, otherTrips, searchKey]);

  /*console.log(searchData.origem);
  console.log(searchData.destino);
  console.log(searchData.encomenda);
  console.log(searchData.seguro);*/

  return (
    <>
      <RenderModal />
      <div className="search-container" style={{ marginBottom: 80 }}>
        <Search initialValues={searchData} />
      </div>
      <div
        className="search-summary"
        onClick={(e) => handleSearchClick(e, searchData)}
      >
        <div className="summary-route">
          <strong>{searchData.origem.split(",")[0]}</strong>
          <span>→</span>
          <strong>{searchData.destino.split(",")[0]}</strong>
        </div>

        <div className="summary-info">
          {searchData.encomenda && <span>📦 {searchData.encomenda}</span>}

          {searchData.seguro && <span>🛡 {searchData.seguro}</span>}

          <div className="summary-edit">✏️ Alterar pesquisa</div>
        </div>
      </div>

      <div className="search-page">
        {loading && <LoadingOverlay />}

        {!!error && (
          <p style={{ textAlign: "center", marginTop: 40, color: "red" }}>
            {error}
          </p>
        )}

        {hasRecommended && (
          <div className="search-top">
            <span className="recommended">⭐ Recomendado para você</span>

            <div className="divider-line"></div>

            <div className="top-filters">
              <span
                className={recommendedSort === "fastest" ? "filter-active" : ""}
              >
                Mais rápido
              </span>

              <label className="switch">
                <input
                  type="checkbox"
                  checked={recommendedSort === "cheapest"}
                  onChange={() =>
                    setRecommendedSort((prev) =>
                      prev === "fastest" ? "cheapest" : "fastest",
                    )
                  }
                />
                <span className="slider"></span>
              </label>

              <span
                className={
                  recommendedSort === "cheapest" ? "filter-active" : ""
                }
              >
                Menos preço
              </span>
            </div>
          </div>
        )}

        {hasRecommended && (
          <div className="cards-list">
            {recommendedTrips.map((item) =>
              renderCard(item, buyTrip, loadingId, searchData),
            )}
          </div>
        )}

        {hasOther && (
          <div className="search-footer">
            <span className="title-other-travels">
              🔵 Outras viagens encontradas
            </span>

            <div className="divider-line"></div>

            <div className="city-filter">
              <span className={!cityMode ? "filter-active" : ""}>
                Cidade Origem
              </span>

              <label className="switch">
                <input
                  type="checkbox"
                  checked={cityMode}
                  onChange={() => setCityMode((prev) => !prev)}
                />
                <span className="slider"></span>
              </label>

              <span className={cityMode ? "filter-active" : ""}>
                Cidade Destino
              </span>
            </div>
          </div>
        )}

        {hasOther && (
          <div className="cards-list">
            {otherTrips.map((item) =>
              renderCard(item, buyTrip, loadingId, searchData),
            )}
          </div>
        )}

        {!loading && !error && !hasRecommended && !hasOther && (
          <p style={{ textAlign: "center", marginTop: 40 }}>
            Nenhuma viagem encontrada 😕
          </p>
        )}
      </div>

      {showNoTravelerModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">
            <div className="modal-header">
              <h2>Sem viajantes no seu itinerário</h2>
            </div>

            <p className="modal-description">
              Lamentamos, mas no momento não temos viajantes no seu itinerário,
              entretanto temos opções de cidades que podem ser úteis.
            </p>

            <p className="modal-question">
              Caso queira ser notificado quando surgir um viajante no seu
              itinerário, marque uma opção:
            </p>

            <div className="radio-group">
              <label className="radio-card">
                <input
                  type="radio"
                  name="notification"
                  value="sim"
                  checked={notificationChoice === "sim"}
                  onChange={() => {
                    setNotificationChoice("sim");
                    setShowNoTravelerModal(false);

                    if (!user) {
                      setShowNotificationModal(true);
                    } else {
                      setShowSuccessModal(true);

                      setTimeout(() => {
                        setShowSuccessModal(false);
                      }, 3000);
                    }
                  }}
                />
                <span>Sim, quero ser notificado</span>
              </label>

              <label className="radio-card">
                <input
                  type="radio"
                  name="notification"
                  value="nao"
                  checked={notificationChoice === "nao"}
                  onChange={() => {
                    setNotificationChoice("nao");
                    setShowNoTravelerModal(false);
                  }}
                />
                <span>Não, obrigado</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {showNotificationModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal contact-modal">
            <div className="modal-header">
              <h2>Contato para Receber Notificações de Viagens</h2>
            </div>

            <p className="modal-description">
              Informe seus dados para ser avisado quando aparecer um viajante
              compatível com seu itinerário.
            </p>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                placeholder="Digite seu email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Telefone</label>
              <input
                type="tel"
                placeholder="Digite seu telefone"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowNotificationModal(false)}
              >
                Cancelar
              </button>

              <button
                className="btn-primary"
                onClick={() => {
                  console.log({ email, telefone });

                  if (email === "" || telefone === "") {
                    return;
                  }

                  setShowNotificationModal(false);
                  setShowSuccessModal(true);

                  setTimeout(() => {
                    setShowSuccessModal(false);
                  }, 3000);
                }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="custom-modal-overlay">
          <div
            className="custom-modal"
            style={{
              maxWidth: 460,
              textAlign: "center",
              padding: "32px 24px",
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                margin: "0 auto 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                boxShadow: "0 10px 30px rgba(34,197,94,0.25)",
                color: "#fff",
                fontSize: 34,
                fontWeight: 700,
              }}
            >
              ✓
            </div>

            <h2
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 700,
                color: "#0f172a",
              }}
            >
              Sucesso!
            </h2>

            <p
              style={{
                marginTop: 14,
                marginBottom: 24,
                fontSize: 15,
                lineHeight: 1.6,
                color: "#475569",
              }}
            >
              Vamos te avisar assim que surgir um viajante compatível com o seu
              itinerário.
            </p>

            <button
              onClick={() => setShowSuccessModal(false)}
              style={{
                border: "none",
                outline: "none",
                padding: "12px 26px",
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                background: "#111827",
                color: "#fff",
                minWidth: 140,
              }}
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
}
